import { isNil } from 'es-toolkit';
import { CompetitorProduct, CompetitorProductOnlyClient, CompetitorMatchedProduct } from '../models';
import { Currency } from '../constants';

/**
 * Gets competitor product with specified currency from competitor data
 * @param competitorData Single product's competitor data
 * @param competitorName Target competitor name (e.g., 'Amazon')
 * @param currency Target currency
 * @returns Single competitor product with matching currency or undefined
 */
export const getCompetitorProduct = (
  competitorData: CompetitorMatchedProduct,
  competitorName: string,
  currency: Currency,
): CompetitorProductOnlyClient | undefined => {
  if (isNil(competitorData) || isNil(competitorName) || isNil(currency)) {
    return undefined;
  }

  // Try human matches first (preferred)
  const humanProducts = competitorData.humanMatchedProducts.find(
    (match) => match.competitor === competitorName,
  )?.competitorProducts;

  if (humanProducts?.length) {
    const lowestPriceProduct = getLowestPriceCompetitorProduct(humanProducts, currency);
    if (lowestPriceProduct) {
      return convertToCompetitorProductFront(lowestPriceProduct, currency);
    }
  }

  // Fallback to machine matches
  const machineProducts = competitorData.machineMatchedProducts.find(
    (match) => match.competitor === competitorName,
  )?.competitorProducts;

  if (machineProducts?.length) {
    const lowestPriceProduct = getLowestPriceCompetitorProduct(machineProducts, currency);
    if (lowestPriceProduct) {
      return convertToCompetitorProductFront(lowestPriceProduct, currency);
    }
  }

  return undefined;
};

/**
 * Finds the product with the lowest price in specified currency
 * @param products Array of competitor products
 * @param currency Target currency
 * @returns Product with lowest price or undefined if no valid products
 */
const getLowestPriceCompetitorProduct = (
  products: CompetitorProduct[],
  currency: Currency,
): CompetitorProduct | undefined => {
  const productsWithPrice = products.filter((product) => product.price.some((p) => p.currency === currency));

  if (productsWithPrice.length === 0) {
    return undefined;
  }

  return productsWithPrice.reduce((lowest, current) => {
    const lowestPrice = lowest.price.find((p) => p.currency === currency)?.salesPrice ?? Infinity;
    const currentPrice = current.price.find((p) => p.currency === currency)?.salesPrice ?? Infinity;

    return currentPrice < lowestPrice ? current : lowest;
  });
};

/**
 * Converts CompetitorProduct to CompetitorProductFront with specific currency
 * @param product Original competitor product
 * @param currency Target currency
 * @returns Frontend-ready product with single price or undefined if currency not found
 */
const convertToCompetitorProductFront = (
  product: CompetitorProduct,
  currency: Currency,
): CompetitorProductOnlyClient | undefined => {
  const priceInfo = product.price.find((p) => p.currency === currency);
  if (!priceInfo) {
    return undefined;
  }

  return {
    ...product,
    price: priceInfo,
  };
};

/**
 * Creates SKU-to-product map for efficient lookups during rendering
 * @param competitorDataArray All competitor match data
 * @param competitorName Target competitor name
 * @param currency Target currency
 * @returns SKU-keyed map of frontend-ready competitor products
 */
export const createCompetitorProductMap = (
  competitorDataArray: CompetitorMatchedProduct[] | undefined,
  competitorName: string,
  currency: Currency,
): Record<string, CompetitorProductOnlyClient> => {
  if (isNil(competitorDataArray) || isNil(competitorName) || isNil(currency)) {
    return {};
  }

  return competitorDataArray.reduce(
    (map, competitorData) => {
      const product = getCompetitorProduct(competitorData, competitorName, currency);
      if (product) {
        map[competitorData.skinSeoulProduct.sku] = product;
      }
      return map;
    },
    {} as Record<string, CompetitorProductOnlyClient>,
  );
};
